import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CalendarCheck, ShieldCheck, AlertTriangle, XCircle, Home } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { IdUpload } from "../components/IdUpload";

const STATUS_LABEL = {
  pending: "Pending", pending_payment: "Pending Payment", pending_verification: "Pending Verification",
  confirmed: "Confirmed",
  checked_in: "Checked In", checked_out: "Checked Out", completed: "Completed",
  cancelled: "Cancelled", refunded: "Refunded",
};

export default function ManageBooking() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [booking, setBooking] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [edit, setEdit] = useState({ check_in: "", check_out: "", guests: 1 });
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const apply = (data) => {
    setBooking(data.booking);
    setPolicy(data.policy);
    setEdit({ check_in: data.booking.check_in, check_out: data.booking.check_out, guests: data.booking.guests });
    setState("ready");
  };

  useEffect(() => {
    api.get(`/manage/${token}`).then((r) => apply(r.data)).catch((e) => {
      setState("error"); toast.error(formatApiError(e.response?.data?.detail));
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/manage/${token}`, { ...edit, guests: Number(edit.guests) });
      apply(data);
      toast.success("Booking updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this booking? Please note the 30% deposit is non-refundable.")) return;
    setCancelling(true);
    try {
      await api.post(`/manage/${token}/cancel`);
      const { data } = await api.get(`/manage/${token}`);
      apply(data);
      toast.success("Booking cancelled");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCancelling(false); }
  };

  const reuploadId = async () => {
    if (!idFile) { toast.error("Please choose a photo ID first."); return; }
    setUploadingId(true);
    try {
      const fd = new FormData(); fd.append("file", idFile);
      await api.post(`/manage/${token}/id-document`, fd);
      setIdFile(null);
      const { data } = await api.get(`/manage/${token}`);
      apply(data);
      toast.success("ID uploaded — our team will review it shortly.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingId(false); }
  };

  return (
    <div className="min-h-screen bg-cream pt-24 pb-16 px-5">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-rose mb-5"><Home size={15} /> The Rose Air BnB</Link>

        {state === "loading" && <div className="text-center py-20"><Loader2 className="animate-spin mx-auto text-rose" /></div>}
        {state === "error" && (
          <div className="card-lux p-10 text-center">
            <XCircle className="mx-auto text-red-400" size={44} />
            <h1 className="font-serif text-3xl text-forest mt-3">Booking not found</h1>
            <p className="text-stone-500 mt-2">This management link is invalid. Please check the link in your confirmation email.</p>
          </div>
        )}

        {state === "ready" && booking && (
          <>
            <div className="card-lux p-7 mb-6" data-testid="manage-summary">
              <div className="flex items-center justify-between">
                <span className="eyebrow">Your reservation</span>
                <span className="text-xs px-3 py-1 rounded-full bg-sand text-forest font-medium">{STATUS_LABEL[booking.status] || booking.status}</span>
              </div>
              <h1 className="font-serif text-3xl text-forest mt-2">{booking.room_name}</h1>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-stone-600">
                <div><span className="text-stone-400">Check-in</span><br />{booking.check_in}</div>
                <div><span className="text-stone-400">Check-out</span><br />{booking.check_out}</div>
                <div><span className="text-stone-400">Guests</span><br />{booking.guests}</div>
                <div><span className="text-stone-400">Total</span><br /><span className="font-semibold text-rose">${booking.total}</span></div>
              </div>
              <div className="text-xs text-stone-400 mt-3">Booking code: <span className="font-mono text-forest">{booking.code || booking.id.slice(0, 8).toUpperCase()}</span></div>
              <div className="text-xs text-amber-700 mt-2">Note: the 30% deposit is non-refundable, including on cancellation.</div>
            </div>

            {/* ID verification */}
            <div className="card-lux p-6 mb-6" data-testid="manage-verification">
              <div className="flex items-center gap-2 mb-2"><ShieldCheck size={18} className="text-gold" /><h2 className="font-serif text-xl text-forest">ID Verification</h2></div>
              {["approved", "overridden"].includes(booking.verification_status) ? (
                <div data-testid="verif-approved" className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 rounded-full px-3 py-1.5 text-sm font-semibold"><ShieldCheck size={15} /> Identity Verified</div>
              ) : booking.verification_status === "rejected" ? (
                <div data-testid="verif-rejected">
                  <p className="text-sm text-red-600 font-medium">Rejected — please upload a new ID.</p>
                  {booking.id_document?.reason && <p className="text-xs text-stone-500 mt-1">Reason: {booking.id_document.reason}</p>}
                  <div className="mt-3"><IdUpload file={idFile} onFile={setIdFile} notice={false} compact /></div>
                  <button onClick={reuploadId} disabled={uploadingId || !idFile} data-testid="verif-reupload" className="btn-primary mt-3 disabled:opacity-60">{uploadingId ? <Loader2 className="animate-spin" size={16} /> : "Upload replacement ID"}</button>
                </div>
              ) : booking.verification_status === "pending" ? (
                <p className="text-sm text-amber-700" data-testid="verif-pending">Pending Verification — our team is reviewing your ID. You'll be emailed once it's approved.</p>
              ) : (
                <div data-testid="verif-none">
                  <p className="text-sm text-stone-500">No photo ID on file. Please upload a valid photo ID to confirm your booking.</p>
                  <div className="mt-3"><IdUpload file={idFile} onFile={setIdFile} notice={false} compact /></div>
                  <button onClick={reuploadId} disabled={uploadingId || !idFile} data-testid="verif-upload" className="btn-primary mt-3 disabled:opacity-60">{uploadingId ? <Loader2 className="animate-spin" size={16} /> : "Upload ID"}</button>
                </div>
              )}
            </div>

            {/* Policy banner */}
            {policy?.can_modify ? (
              <div className="rounded-xl bg-green-50 text-green-800 px-5 py-3 text-sm flex items-center gap-2 mb-6" data-testid="manage-policy">
                <ShieldCheck size={18} /> You can modify or cancel this booking online until <strong>{policy.deadline}</strong> (within {policy.window_days} days of booking).
              </div>
            ) : (
              <div className="rounded-xl bg-amber-50 text-amber-800 px-5 py-3 text-sm flex items-center gap-2 mb-6" data-testid="manage-policy">
                <AlertTriangle size={18} /> {policy?.reason || "Online changes are not available for this booking."}
              </div>
            )}

            {policy?.can_modify && (
              <form onSubmit={save} className="card-lux p-7 mb-6" data-testid="manage-edit-form">
                <h2 className="font-serif text-2xl text-forest mb-4 flex items-center gap-2"><CalendarCheck className="text-rose" size={20} /> Modify booking</h2>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wide text-stone-500">Check in</label>
                    <input type="date" min={today} value={edit.check_in} data-testid="manage-checkin" onChange={(e) => setEdit({ ...edit, check_in: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wide text-stone-500">Check out</label>
                    <input type="date" min={edit.check_in || today} value={edit.check_out} data-testid="manage-checkout" onChange={(e) => setEdit({ ...edit, check_out: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wide text-stone-500">Guests</label>
                    <input type="number" min={1} max={12} value={edit.guests} data-testid="manage-guests" onChange={(e) => setEdit({ ...edit, guests: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-5">
                  <button type="submit" disabled={saving} data-testid="manage-save" className="btn-primary disabled:opacity-60">
                    {saving ? <Loader2 className="animate-spin" size={16} /> : "Save changes"}
                  </button>
                  <button type="button" onClick={cancel} disabled={cancelling} data-testid="manage-cancel"
                    className="inline-flex items-center gap-2 rounded-full border border-red-200 text-red-600 px-6 py-3 text-sm font-semibold hover:bg-red-50 disabled:opacity-60">
                    {cancelling ? <Loader2 className="animate-spin" size={16} /> : "Cancel booking"}
                  </button>
                </div>
              </form>
            )}

            <p className="text-sm text-stone-500 text-center">Need help? <Link to="/contact" className="text-rose font-semibold">Contact our team</Link>.</p>
          </>
        )}
      </div>
    </div>
  );
}
